import { useState, useEffect } from 'react';
import { Plus, Loader2, Edit, Trash2, MoreHorizontal } from 'lucide-react';
import { useTeamMembers } from '../../Hooks/useTeamMembers';
import TeamMemberModal from './components/TeamMemberModal';
import DeleteTeamMemberModal from './components/DeleteTeamMemberModal';
import { canAccess } from '../../utils/permissions';
import { UserGroupIcon } from '@heroicons/react/24/outline';

export default function TeamMember() {
  const {
    permissions,
    teamMembers,
    isLoadingPermissions,
    isLoadingMembers,
    permissionsError,
    membersError,
    createTeamMember,
    updateTeamMember,
    deleteTeamMember,
    isCreating,
    isUpdating,
    isDeleting,
    refetchMembers,
    refetchPermissions,
  } = useTeamMembers();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const canCreate = canAccess('create_team_member');
  const canUpdate = canAccess('update_team_member');
  const canDelete = canAccess('delete_team_member');

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (!canAccess("view_team_members")) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
          <UserGroupIcon className="w-10 h-10 text-red-600" />
        </div>
        <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
        <p className="text-gray-500 max-w-md font-medium">
          You don't have the required permissions to view team members.
        </p>
      </div>
    );
  }

  const handleAddClick = () => {
    setSelectedMember(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (member) => {
    setSelectedMember(member);
    setIsModalOpen(true);
    setOpenMenuId(null);
  };

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setIsDeleteModalOpen(true);
    setOpenMenuId(null);
  };

  const handleModalSubmit = (data) => {
    if (selectedMember) {
      updateTeamMember(
        { id: selectedMember.id, data },
        {
          onSuccess: () => {
            setIsModalOpen(false);
            setSelectedMember(null);
          },
        }
      );
    } else {
      createTeamMember(data, {
        onSuccess: () => {
          setIsModalOpen(false);
        },
      });
    }
  };

  const handleConfirmDelete = () => {
    if (memberToDelete) {
      deleteTeamMember(memberToDelete.id, {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          setMemberToDelete(null);
        },
      });
    }
  };


  const isLoading = isLoadingMembers || isLoadingPermissions;
  const hasError = membersError || permissionsError;

  return (
    <section className="min-h-screen text-sm">
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Team Members</h1>
            <p className="text-xs text-gray-600">Manage team member access and permissions</p>
          </div>

          {canCreate && (
            <button
              onClick={handleAddClick}
              className="px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-md hover:shadow-lg transition-shadow"
            >
              <Plus className="w-4 h-4" />
              Add Team Member
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-5">
        {permissionsError && (
          <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4 mb-4">
            <p className="text-red-600 text-xs mb-2">
              Failed to load permissions. Please try again.
            </p>
            <button
              onClick={refetchPermissions}
              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs hover:bg-red-700 transition"
            >
              Retry
            </button>
          </div>
        )}

        {membersError && !permissionsError && (
          <div className="bg-white rounded-xl shadow-sm border border-red-200 p-4 mb-4">
            <p className="text-red-600 text-xs mb-2">
              Failed to load team members. Please try again.
            </p>
            <button
              onClick={refetchMembers}
              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs hover:bg-red-700 transition"
            >
              Retry
            </button>
          </div>
        )}

        {isLoading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-20 text-center">
            <Loader2 className="w-12 h-12 mx-auto text-teal-600 animate-spin" />
            <p className="mt-4 text-gray-600 text-sm">Loading team members...</p>
          </div>
        )}

        {!isLoading && !hasError && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden text-xs">
            {teamMembers.length === 0 ? (
              <div className="p-20 text-center text-gray-500">
                <p className="text-lg font-medium">No team members found</p>
                <p className="text-sm mt-2">
                  Add your first team member to get started
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-700">
                  <tr>
                    <th className="text-left px-3 py-3">Name</th>
                    <th className="text-left px-3 py-3">Email</th>
                    <th className="text-left px-3 py-3">Permissions</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map((member) => (
                    <tr
                      key={member.id}
                      className="border-b border-gray-100 hover:bg-gray-50 transition"
                    >
                      <td className="px-3 py-3 font-medium text-gray-900">
                        {member.name}
                      </td>
                      <td className="px-3 py-3 text-gray-700">{member.email}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap gap-1.5 max-w-md">
                          {!member.permissions || member.permissions.length === 0 ? (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                              No permissions
                            </span>
                          ) : (
                            member.permissions.map((permission, index) => (
                              <span
                                key={permission.id || index}
                                className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium"
                              >
                                {permission.name || permission.label || 'Permission'}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                      {(canUpdate || canDelete) && (
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-end">
                            {openMenuId === member.id && (
                              <div className="flex items-center bg-white rounded-lg shadow-md border border-gray-200 p-0.5 mr-1">
                                {canUpdate && (
                                  <button
                                    onClick={() => handleEditClick(member)}
                                    className="p-2 hover:bg-gray-100 rounded transition"
                                    title="Edit"
                                  >
                                    <Edit className="w-4 h-4 text-gray-600" />
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    onClick={() => handleDeleteClick(member)}
                                    className="p-2 hover:bg-gray-100 rounded transition"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </button>
                                )}
                              </div>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(openMenuId === member.id ? null : member.id);
                              }}
                              className="p-1.5 hover:bg-gray-100 rounded transition"
                            >
                              <MoreHorizontal className="w-4 h-4 text-gray-500" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <TeamMemberModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedMember(null);
        }}
        onSubmit={handleModalSubmit}
        member={selectedMember}
        permissions={permissions}
        isLoading={isCreating || isUpdating}
      />

      <DeleteTeamMemberModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setMemberToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        memberName={memberToDelete?.name || ''}
        isLoading={isDeleting}
      />
    </section>
  );
}
